import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar as CalIcon, Check, Loader2, Minus, Plus } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";

const STEPS = ["service", "schedule", "details", "review"];

const TRICITY_CITIES = [
  "Bytów",
  "Chojnice",
  "Człuchów",
  "Gdańsk",
  "Gdynia",
  "Hel",
  "Jastarnia",
  "Kartuzy",
  "Kościerzyna",
  "Kwidzyn",
  "Lębork",
  "Malbork",
  "Nowy Dwór Gdański",
  "Pelplin",
  "Pruszcz Gdański",
  "Puck",
  "Reda",
  "Rumia",
  "Skarszewy",
  "Słupsk",
  "Sopot",
  "Starogard Gdański",
  "Sztum",
  "Tczew",
  "Wejherowo",
  "Władysławowo",
  "Żukowo",
];

function formatDate(d) {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Pomeranian Voivodeship postal-code prefixes (Tri-City + surroundings).
// Anything outside these prefixes is shown as a soft "we may not cover this"
// warning — it does NOT block the booking, customers can still proceed.
const COVERED_POSTAL_PREFIXES = [
  "80", "81", "82", "83", "84", "76", "77", "89",
];
const POSTAL_RE = /^\d{2}-\d{3}$/;

function postalCoverageState(code) {
  const cleaned = (code || "").trim();
  if (!cleaned) return "empty";
  if (!POSTAL_RE.test(cleaned)) return "invalid";
  const prefix = cleaned.slice(0, 2);
  return COVERED_POSTAL_PREFIXES.includes(prefix) ? "covered" : "outside";
}

export default function Booking() {
  const { t, lang } = useI18n();
  useSEO({ titleKey: "seo.booking_title", descriptionKey: "seo.booking_desc" });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialSlug = params.get("service");

  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [cart, setCart] = useState({}); // {service_id: qty}
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [blocked, setBlocked] = useState(false);
  const [time, setTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [details, setDetails] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address_street: "",
    address_city: "Gdańsk",
    address_postal: "",
    notes: "",
    payment_method: "cash",
    has_outdoor_cold_tap: false,
    has_outdoor_hot_tap: false,
  });
  const [errors, setErrors] = useState({});
  const [bannerErrorKey, setBannerErrorKey] = useState("");
  const [bannerErrorMsg, setBannerErrorMsg] = useState(""); // raw fallback for unmapped server errors

  const clearError = (field) =>
    setErrors((e) => {
      if (!e[field]) return e;
      const out = { ...e };
      delete out[field];
      return out;
    });

  const clearBanner = () => {
    setBannerErrorKey("");
    setBannerErrorMsg("");
  };

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      if (initialSlug) {
        const found = r.data.find((s) => s.slug === initialSlug);
        if (found) setCart({ [found.id]: 1 });
      }
    });
  }, [initialSlug]);

  useEffect(() => {
    if (!date) return;
    const ds = formatDate(date);
    setLoadingSlots(true);
    api
      .get(`/availability?date=${ds}`)
      .then((r) => {
        setSlots(r.data.slots || []);
        setBlocked(r.data.blocked);
        setTime("");
      })
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const total = useMemo(
    () =>
      Object.entries(cart).reduce((sum, [id, qty]) => {
        const s = services.find((x) => x.id === id);
        return sum + (s ? s.base_price * qty : 0);
      }, 0),
    [cart, services],
  );

  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);

  const inc = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) =>
    setCart((c) => {
      const next = (c[id] || 0) - 1;
      const out = { ...c };
      if (next <= 0) delete out[id];
      else out[id] = next;
      return out;
    });

  const canNext = () => {
    if (step === 0) return cartCount > 0;
    if (step === 1) return !!date && !!time && !blocked;
    if (step === 2)
      return (
        details.customer_name.trim() &&
        details.customer_email.trim() &&
        details.customer_phone.trim() &&
        details.address_street.trim() &&
        details.address_postal.trim() &&
        details.has_outdoor_cold_tap
      );
    return true;
  };

  const validateDetailsStep = () => {
    const e = {};
    const reqMsg = t("errors.required");
    if (!details.customer_name.trim()) e.customer_name = reqMsg;
    if (!details.customer_email.trim()) e.customer_email = reqMsg;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(details.customer_email.trim()))
      e.customer_email = t("errors.email_invalid");
    if (!details.customer_phone.trim()) e.customer_phone = reqMsg;
    else if (details.customer_phone.replace(/\D/g, "").length < 7)
      e.customer_phone = t("errors.phone_short");
    if (!details.address_street.trim()) e.address_street = reqMsg;
    if (!details.address_postal.trim()) e.address_postal = reqMsg;
    if (!details.has_outdoor_cold_tap)
      e.has_outdoor_cold_tap = t("errors.cold_tap_required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    clearBanner();
    if (step === 2 && !validateDetailsStep()) {
      setBannerErrorKey("errors.generic");
      return;
    }
    setStep(step + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    clearBanner();
    // re-validate the details one more time to catch edits made on the review step path
    if (!validateDetailsStep()) {
      setBannerErrorKey("errors.generic");
      setStep(2);
      setSubmitting(false);
      return;
    }
    try {
      const items = Object.entries(cart).map(([service_id, quantity]) => ({
        service_id,
        quantity,
      }));
      const payload = {
        items,
        ...details,
        scheduled_date: formatDate(date),
        scheduled_time: time,
        language: lang,
      };
      const { data } = await api.post("/bookings", payload);
      // Stash for instant render on the confirmation page (no API round-trip).
      try {
        sessionStorage.setItem(`lh2o_booking_${data.id}`, JSON.stringify(data));
      } catch (_) { /* ignore quota errors */ }
      navigate(`/book/confirmation/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "";
      const lower = msg.toLowerCase();
      if (lower.includes("slot")) {
        setBannerErrorKey("errors.slot_taken");
        setTime("");
        setStep(1);
      } else if (lower.includes("date") || lower.includes("unavailable")) {
        setBannerErrorKey("errors.date_unavailable");
        setDate(undefined);
        setStep(1);
      } else {
        setBannerErrorKey("errors.generic");
        setBannerErrorMsg(msg);
        setStep(2);
      }
      toast.error(msg || t("errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-water-grad py-8 sm:py-12" data-testid="booking-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-5">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          {t("booking.title")}
        </h1>

        {/* Stepper */}
        <div className="mt-7 flex items-center gap-2" data-testid="booking-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={`flex items-center gap-2 text-sm font-medium ${
                  i <= step ? "text-sky-700" : "text-slate-400"
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${
                    i < step
                      ? "bg-sky-600 text-white"
                      : i === step
                        ? "bg-sky-100 text-sky-700 ring-2 ring-sky-600"
                        : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className="hidden sm:inline">{t(`booking.step_${s}`)}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-px ${i < step ? "bg-sky-600" : "bg-slate-200"}`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-8 rounded-3xl bg-white border border-slate-200 shadow-sm p-4 sm:p-6 lg:p-8">
          {bannerErrorKey && (
            <div
              className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2"
              data-testid="booking-error-banner"
              role="alert"
            >
              <span className="mt-0.5 font-bold">!</span>
              <span>{t(bannerErrorKey)}</span>
            </div>
          )}
          {step === 0 && (
            <div data-testid="step-service">
              <h2 className="font-display text-xl font-semibold">
                {t("booking.pick_services")}
              </h2>
              <div className="mt-5 space-y-3">
                {services.map((s) => {
                  const qty = cart[s.id] || 0;
                  const perM2 = s.pricing_mode === "per_m2";
                  if (perM2) {
                    const subtotal = (qty || 0) * s.base_price;
                    return (
                      <div
                        key={s.id}
                        className={`rounded-2xl border p-4 transition ${
                          qty > 0
                            ? "border-sky-500 bg-sky-50/50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`service-row-${s.slug}`}
                      >
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="min-w-0">
                            <div className="font-display text-lg font-semibold">
                              {lang === "pl" ? s.name_pl : s.name_en}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {s.base_price.toFixed(0)} {t("common.currency")} /{" "}
                              {lang === "pl" ? s.unit_pl : s.unit_en}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label
                              htmlFor={`area-${s.id}`}
                              className="text-xs text-slate-600 whitespace-nowrap"
                            >
                              {t("booking.area_label")}
                            </Label>
                            <div className="relative">
                              <Input
                                id={`area-${s.id}`}
                                type="number"
                                min="0"
                                step="1"
                                inputMode="numeric"
                                placeholder={t("booking.area_placeholder")}
                                value={qty || ""}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  setCart((c) => {
                                    const out = { ...c };
                                    if (!v || v <= 0) delete out[s.id];
                                    else out[s.id] = v;
                                    return out;
                                  });
                                }}
                                className="w-28 pr-10"
                                data-testid={`area-input-${s.slug}`}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
                                {t("booking.area_unit")}
                              </span>
                            </div>
                          </div>
                        </div>
                        {qty > 0 && (
                          <div
                            className="mt-3 flex items-center justify-between rounded-lg bg-white border border-sky-200 px-3 py-2"
                            data-testid={`area-subtotal-${s.slug}`}
                          >
                            <span className="text-xs text-slate-500">
                              {qty} m² × {s.base_price.toFixed(0)}{" "}
                              {t("common.currency")}
                            </span>
                            <span className="font-display font-bold text-sky-700">
                              ≈ {subtotal.toFixed(0)} {t("common.currency")}
                            </span>
                          </div>
                        )}
                        <p className="text-[11px] text-slate-400 mt-2">
                          {t("booking.area_min_hint")}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={s.id}
                      className={`rounded-2xl border p-4 transition ${
                        qty > 0
                          ? "border-sky-500 bg-sky-50/50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`service-row-${s.slug}`}
                    >
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-lg font-semibold">
                            {lang === "pl" ? s.name_pl : s.name_en}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {s.base_price.toFixed(0)} {t("common.currency")} /{" "}
                            {lang === "pl" ? s.unit_pl : s.unit_en}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-600 hidden sm:inline">
                            {t("booking.bin_label")}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => dec(s.id)}
                              disabled={qty === 0}
                              className="w-9 h-9 rounded-lg border border-slate-300 grid place-items-center disabled:opacity-40 hover:bg-slate-50"
                              data-testid={`dec-${s.slug}`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span
                              className="w-7 text-center font-bold"
                              data-testid={`qty-${s.slug}`}
                            >
                              {qty}
                            </span>
                            <button
                              onClick={() => inc(s.id)}
                              className="w-9 h-9 rounded-lg bg-sky-600 text-white grid place-items-center hover:bg-sky-700"
                              data-testid={`inc-${s.slug}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {qty > 0 && (
                        <div
                          className="mt-3 flex items-center justify-between rounded-lg bg-white border border-sky-200 px-3 py-2"
                          data-testid={`bin-subtotal-${s.slug}`}
                        >
                          <span className="text-xs text-slate-500">
                            {qty}{" "}
                            {qty === 1
                              ? t("booking.bin_unit_singular")
                              : t("booking.bin_unit_plural")}{" "}
                            × {s.base_price.toFixed(0)} {t("common.currency")}
                          </span>
                          <span className="font-display font-bold text-sky-700">
                            {(qty * s.base_price).toFixed(0)}{" "}
                            {t("common.currency")}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-schedule">
              <h2 className="font-display text-xl font-semibold flex items-center gap-2">
                <CalIcon className="w-5 h-5 text-sky-600" />
                {t("booking.pick_date")}
              </h2>
              <div className="mt-4 grid md:grid-cols-2 gap-5 sm:gap-6">
                <div className="rounded-xl border border-slate-200 p-2 sm:p-3 bg-white overflow-x-auto">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    data-testid="booking-calendar"
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-700 mb-3">
                    {t("booking.pick_time")}
                  </h3>
                  {!date && (
                    <div className="text-sm text-slate-500">
                      {t("booking.pick_date")} →
                    </div>
                  )}
                  {date && loadingSlots && (
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading slots…
                    </div>
                  )}
                  {date && !loadingSlots && blocked && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 p-4 text-sm">
                      {t("booking.blocked")}
                    </div>
                  )}
                  {date && !loadingSlots && !blocked && (
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          disabled={!s.available}
                          onClick={() => setTime(s.time)}
                          className={`px-3 py-2.5 rounded-lg text-sm font-semibold border transition ${
                            time === s.time
                              ? "bg-sky-600 text-white border-sky-600"
                              : s.available
                                ? "border-slate-300 hover:border-sky-500 hover:text-sky-700"
                                : "border-slate-200 bg-slate-50 text-slate-400 line-through cursor-not-allowed"
                          }`}
                          data-testid={`slot-${s.time}`}
                        >
                          {s.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-details" className="space-y-5">
              <h2 className="font-display text-xl font-semibold">
                {t("booking.contact_info")}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t("booking.name")}</Label>
                  <Input
                    value={details.customer_name}
                    onChange={(e) => {
                      setDetails({ ...details, customer_name: e.target.value });
                      clearError("customer_name");
                    }}
                    className={
                      errors.customer_name
                        ? "border-rose-400 focus-visible:ring-rose-500"
                        : ""
                    }
                    aria-invalid={!!errors.customer_name}
                    data-testid="input-name"
                  />
                  {errors.customer_name && (
                    <p
                      className="mt-1 text-xs text-rose-600"
                      data-testid="error-customer_name"
                    >
                      {errors.customer_name}
                    </p>
                  )}
                </div>
                <div>
                  <Label>{t("booking.email")}</Label>
                  <Input
                    type="email"
                    value={details.customer_email}
                    onChange={(e) => {
                      setDetails({ ...details, customer_email: e.target.value });
                      clearError("customer_email");
                    }}
                    className={
                      errors.customer_email
                        ? "border-rose-400 focus-visible:ring-rose-500"
                        : ""
                    }
                    aria-invalid={!!errors.customer_email}
                    data-testid="input-email"
                  />
                  {errors.customer_email && (
                    <p
                      className="mt-1 text-xs text-rose-600"
                      data-testid="error-customer_email"
                    >
                      {errors.customer_email}
                    </p>
                  )}
                </div>
                <div>
                  <Label>{t("booking.phone")}</Label>
                  <Input
                    value={details.customer_phone}
                    onChange={(e) => {
                      setDetails({ ...details, customer_phone: e.target.value });
                      clearError("customer_phone");
                    }}
                    className={
                      errors.customer_phone
                        ? "border-rose-400 focus-visible:ring-rose-500"
                        : ""
                    }
                    aria-invalid={!!errors.customer_phone}
                    data-testid="input-phone"
                  />
                  {errors.customer_phone && (
                    <p
                      className="mt-1 text-xs text-rose-600"
                      data-testid="error-customer_phone"
                    >
                      {errors.customer_phone}
                    </p>
                  )}
                </div>
              </div>
              <h2 className="font-display text-xl font-semibold pt-2">
                {t("booking.address")}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>{t("booking.street")}</Label>
                  <Input
                    value={details.address_street}
                    onChange={(e) => {
                      setDetails({ ...details, address_street: e.target.value });
                      clearError("address_street");
                    }}
                    className={
                      errors.address_street
                        ? "border-rose-400 focus-visible:ring-rose-500"
                        : ""
                    }
                    aria-invalid={!!errors.address_street}
                    data-testid="input-street"
                  />
                  {errors.address_street && (
                    <p
                      className="mt-1 text-xs text-rose-600"
                      data-testid="error-address_street"
                    >
                      {errors.address_street}
                    </p>
                  )}
                </div>
                <div>
                  <Label>{t("booking.city")}</Label>
                  <Select
                    value={details.address_city}
                    onValueChange={(v) =>
                      setDetails({ ...details, address_city: v })
                    }
                  >
                    <SelectTrigger
                      className="mt-1"
                      data-testid="select-city"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRICITY_CITIES.map((c) => (
                        <SelectItem
                          key={c}
                          value={c}
                          data-testid={`city-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("booking.postal")}</Label>
                  <Input
                    value={details.address_postal}
                    onChange={(e) =>
                      setDetails({ ...details, address_postal: e.target.value })
                    }
                    placeholder="80-001"
                    inputMode="numeric"
                    data-testid="input-postal"
                  />
                  {(() => {
                    const state = postalCoverageState(details.address_postal);
                    if (state === "invalid") {
                      return (
                        <p
                          className="mt-1 text-xs text-rose-600"
                          data-testid="postal-invalid"
                        >
                          {t("booking.postal_invalid")}
                        </p>
                      );
                    }
                    if (state === "outside") {
                      return (
                        <p
                          className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5"
                          data-testid="postal-outside-area"
                        >
                          {t("booking.postal_outside_area")}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
              <div>
                <Label>{t("booking.notes")}</Label>
                <Textarea
                  value={details.notes}
                  onChange={(e) =>
                    setDetails({ ...details, notes: e.target.value })
                  }
                  rows={3}
                  data-testid="input-notes"
                />
              </div>
              <div>
                <Label className="mb-2 block">{t("booking.payment")}</Label>
                <RadioGroup
                  value={details.payment_method}
                  onValueChange={(v) =>
                    setDetails({ ...details, payment_method: v })
                  }
                  className="grid sm:grid-cols-2 gap-3"
                >
                  <label
                    className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${
                      details.payment_method === "cash"
                        ? "border-sky-500 bg-sky-50"
                        : "border-slate-200"
                    }`}
                  >
                    <RadioGroupItem value="cash" data-testid="payment-cash" />
                    <span className="text-sm">{t("booking.cash")}</span>
                  </label>
                  <label
                    className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${
                      details.payment_method === "bank_transfer"
                        ? "border-sky-500 bg-sky-50"
                        : "border-slate-200"
                    }`}
                  >
                    <RadioGroupItem
                      value="bank_transfer"
                      data-testid="payment-bank"
                    />
                    <span className="text-sm">{t("booking.bank")}</span>
                  </label>
                </RadioGroup>
              </div>

              <div className="rounded-2xl bg-sky-50/60 border border-sky-200 p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {t("booking.water_section")}
                  </h3>
                  <p className="text-xs text-slate-600 mt-1">
                    {t("booking.water_intro")}
                  </p>
                </div>
                <label
                  className={`flex items-start gap-3 rounded-xl bg-white border p-3 cursor-pointer ${
                    errors.has_outdoor_cold_tap
                      ? "border-rose-400"
                      : "border-slate-200"
                  }`}
                  data-testid="tap-cold-row"
                >
                  <Checkbox
                    checked={details.has_outdoor_cold_tap}
                    onCheckedChange={(v) => {
                      setDetails({ ...details, has_outdoor_cold_tap: !!v });
                      clearError("has_outdoor_cold_tap");
                    }}
                    className="mt-0.5"
                    data-testid="checkbox-cold-tap"
                  />
                  <span className="text-sm text-slate-700 leading-snug">
                    {t("booking.has_cold")}
                  </span>
                </label>
                <label
                  className="flex items-start gap-3 rounded-xl bg-white border border-slate-200 p-3 cursor-pointer"
                  data-testid="tap-hot-row"
                >
                  <Checkbox
                    checked={details.has_outdoor_hot_tap}
                    onCheckedChange={(v) =>
                      setDetails({ ...details, has_outdoor_hot_tap: !!v })
                    }
                    className="mt-0.5"
                    data-testid="checkbox-hot-tap"
                  />
                  <span className="text-sm text-slate-700 leading-snug">
                    {t("booking.has_hot")}
                  </span>
                </label>
                {!details.has_outdoor_cold_tap && (
                  <p
                    className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                    data-testid="tap-warning"
                  >
                    {errors.has_outdoor_cold_tap || t("booking.no_tap_warning")}
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-review" className="space-y-4">
              <h2 className="font-display text-xl font-semibold">
                {t("booking.review_title")}
              </h2>
              <div className="rounded-2xl border border-slate-200 p-4">
                {Object.entries(cart).map(([id, qty]) => {
                  const s = services.find((x) => x.id === id);
                  if (!s) return null;
                  return (
                    <div
                      key={id}
                      className="flex items-center justify-between py-2"
                    >
                      <div className="text-sm">
                        <span className="font-semibold">
                          {qty} ×{" "}
                          {lang === "pl" ? s.name_pl : s.name_en}
                        </span>
                      </div>
                      <div className="font-semibold">
                        {(s.base_price * qty).toFixed(0)} {t("common.currency")}
                      </div>
                    </div>
                  );
                })}
                <div className="border-t border-slate-200 mt-2 pt-3 flex items-center justify-between">
                  <span className="text-slate-600">{t("booking.total")}</span>
                  <span className="font-display text-2xl font-bold text-sky-700">
                    {total.toFixed(0)} {t("common.currency")}
                  </span>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 p-4 text-sm text-slate-700 grid sm:grid-cols-2 gap-2">
                <div>
                  <span className="text-slate-500">{t("booking.name")}: </span>
                  {details.customer_name}
                </div>
                <div>
                  <span className="text-slate-500">{t("booking.email")}: </span>
                  {details.customer_email}
                </div>
                <div>
                  <span className="text-slate-500">{t("booking.phone")}: </span>
                  {details.customer_phone}
                </div>
                <div>
                  <span className="text-slate-500">{t("booking.address")}: </span>
                  {details.address_street}, {details.address_postal}{" "}
                  {details.address_city}
                </div>
                <div>
                  <span className="text-slate-500">{t("booking.pick_date")}: </span>
                  {formatDate(date)} {time}
                </div>
                <div>
                  <span className="text-slate-500">{t("booking.payment")}: </span>
                  {details.payment_method === "cash"
                    ? t("booking.cash")
                    : t("booking.bank")}
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-semibold"
              data-testid="booking-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> {t("booking.back")}
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={step !== 2 && !canNext()}
                className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="booking-next-btn"
              >
                {t("booking.next")} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="booking-confirm-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("booking.confirm")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
